import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { REGISTER, PUT_CHANGE_PARTNER, GET_AFFILIATES_BRANDING } from 'api/paths/constants';
import { CREATE_CHANGE_PARTNER_PUT } from 'api/paths/constants';
import loginRequest from 'api/requests/login';
import { RootState } from 'store/types';
import { authStorage } from 'store/storage';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import fetcher from 'utils/fetcher';
import { ApiResponseType } from 'types';
import { EMPTY_OBJECT } from 'constant';
import { dispatchLogoutEvent } from 'utils/logoutListener';

import {
  AuthLoginResponseType,
  AuthLoginSchemaType,
  UseAuthStateType,
  UseAuthActionsType,
  IAffiliate,
  AuthState,
  BrandingType,
} from './types';
import { LOGOUT_ACTION_TYPE } from './actionTypes';

const initialState: UseAuthStateType = {
  isLoading: false,
  branding: null,
  data: authStorage.get(),
};

const getBranding = createAsyncThunk<ApiResponseType<BrandingType>>(
  GET_AFFILIATES_BRANDING,
  async (body, { rejectWithValue }) => {
    try {
      const response = await fetcher<BrandingType>({
        method: 'GET',
        url: GET_AFFILIATES_BRANDING,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const login = createAsyncThunk<ApiResponseType<AuthLoginResponseType>, AuthLoginSchemaType>(
  'affiliate/login',
  async (body, { rejectWithValue }) => {
    try {
      return await loginRequest(body);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const signUp = createAsyncThunk<AuthLoginResponseType, AuthLoginSchemaType>(
  REGISTER,
  async (body, { rejectWithValue }) => {
    try {
      const response = await fetcher<
        {
          accessToken: string;
          affiliate: IAffiliate;
        },
        AuthLoginSchemaType
      >({
        url: REGISTER,
        method: 'POST',
        body,
      });

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const changePartner = createAsyncThunk<AuthLoginResponseType, number>(
  PUT_CHANGE_PARTNER,
  async (body, { rejectWithValue }) => {
    try {
      const response = await fetcher<
        {
          accessToken: string;
          affiliate: IAffiliate;
        },
        AuthLoginSchemaType
      >({
        url: CREATE_CHANGE_PARTNER_PUT(body),
        method: 'PUT',
      });

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (): void => {
      localStorage.setItem('manualLogout', 'true');
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(login.pending.type, (state): void => {
        state.isLoading = true;
      })
      .addCase(login.fulfilled.type, (state, action: PayloadAction<{ data: AuthState }>): void => {
        if (action.payload?.data?.affiliate?.id) {
          authStorage.set(action.payload.data);
          state.data = action.payload?.data;
        }
        state.isLoading = false;
      })
      .addCase(login.rejected.type, (state, action: PayloadAction<ApiResponseType<null>>): void => {
        state.data = EMPTY_OBJECT;
        state.error = action.payload;
        state.isLoading = false;
      })
      .addCase(changePartner.fulfilled.type, (state, action: PayloadAction<{ data: AuthState }>): void => {
        if (action.payload?.data?.affiliate?.id) {
          authStorage.set(action.payload.data);
          state.data = action.payload?.data;
        }
      })
      .addCase(getBranding.fulfilled.type, (state, action: PayloadAction<{ data: BrandingType }>): void => {
        if (state.data) {
          state.branding = action.payload.data;
          const lStorage = authStorage.get();
          authStorage.set({ ...lStorage, branding: action.payload.data });
        }
      })
      .addCase(signUp.pending.type, (state): void => {
        state.isLoading = true;
      })
      .addCase(signUp.fulfilled.type, (state): void => {
        state.isLoading = false;
      })
      .addCase(signUp.rejected.type, (state): void => {
        state.isLoading = false;
      }),
});

const useAuth = (): [UseAuthStateType, UseAuthActionsType] => {
  const state = useAppSelector((store: RootState) => store.auth);
  const dispatch = useAppDispatch();

  const actions = {
    login: async (params: AuthLoginSchemaType): Promise<{ data: AuthState; status: number }> => {
      const response = await dispatch(login(params));
      return response.payload as Promise<{ data: AuthState; status: number }>;
    },
    changePartner: async (params: number): Promise<void> => {
      await dispatch(changePartner(params));
    },
    logout: (): void => {
      dispatch(authSlice.actions.logout());

      setTimeout(() => {
        authStorage.clear();
        dispatch({ type: LOGOUT_ACTION_TYPE });
        dispatchLogoutEvent();
      }, 200);
    },
    getBranding: async (): Promise<void> => {
      await dispatch(getBranding());
    },
    signUp: async (params: AuthLoginSchemaType): Promise<{ status: number; code?: number; text: string }> => {
      const response = await dispatch(signUp(params));
      return response.payload as Promise<{ status: number; code?: number; text: string }>;
    },
  };

  return [state, actions];
};

export { authSlice };

export * from './types';

export default useAuth;
